/**
 * Services Client
 * Handles 1C integration endpoints for sales search, document management, and Excel processing.
 * Follows Custom Method Pattern from client factory architecture (Pattern 3).
 * @module api/clients/services
 */
import api from "@/services/api"
import { buildEndpoint } from "@/services/endpoints"

// LLM:METADATA
// :hierarchy: [Domain:1CIntegration | API_Client | Factory]
// :relates-to: uses: "api.js", required-by: "composables/useServices.js"
// :rationale: "Handles 1C integration endpoints including sales search, document management, templates, and Excel revaluation processing. Uses custom pattern instead of contractClient due to FormData file uploads and varied response types requiring direct axios configuration."
// :contract: pre: "User authenticated with 1C access permissions", post: "1C data retrieved or operation completed", invariant: "All requests include proper authentication headers"
// :decision_cache: "Chose custom pattern (Pattern 3) instead of contractClient for FormData file handling and mixed response types (JSON + file downloads). Direct api calls allow flexible config (responseType, headers) per endpoint. [SERVICES-CLIENT-001]"
// :references: SPEC: "Phase 3B Client Factory Implementation", ARCH: "client-factory-architecture.md Pattern 3"
// LLM:END

/**
 * 1C Services endpoint paths
 * @note Migrated from /edit/services/* to /api/services/* (2026-02-23)
 */
const ENDPOINTS = {
  SALES: buildEndpoint("services.sales"),
  LOAD_SALES: buildEndpoint("services.salesLoad"),
  SUBMIT_SALES: buildEndpoint("services.salesSubmit"),
  GET_DOCUMENTS: buildEndpoint("services.get1CDocuments"),
  GET_TEMPLATES: buildEndpoint("services.get1CTemplates"),
  GET_SOURCES: buildEndpoint("services.get1CSources"),
  PROCESS_REVALUATION: buildEndpoint("services.revaluationProcess"),
}

/**
 * Normalize one raw 1C template row into the SPA's canonical template shape.
 *
 * @param {Record<string, any>} template
 * @returns {{ code: string, name: string, date?: string } | null}
 * @precondition Template is either the legacy `{Номер, Шаблон, ДатаДокумента}` row or an already-canonical `{code, name}` object.
 * @postcondition Returns `null` when the row cannot produce a non-empty template code and name.
 * @mutates none
 */
export function normalize1CTemplate(template) {
  if (!template || typeof template !== "object") {
    return null
  }

  const code = String(template.code ?? template["Номер"] ?? "").trim()
  const name = String(template.name ?? template["Шаблон"] ?? "").trim()
  const date = String(template.date ?? template["ДатаДокумента"] ?? "").trim()

  if (!code || !name) {
    return null
  }

  return date ? { code, name, date } : { code, name }
}

/**
 * Normalize the raw 1C template list into canonical SPA rows.
 *
 * @param {unknown} templates
 * @returns {Array<{ code: string, name: string, date?: string }>}
 * @precondition Caller passes the raw backend payload for `/api/services/1c-templates`.
 * @postcondition Invalid rows are discarded; valid rows preserve only the canonical UI fields.
 * @mutates none
 */
export function normalize1CTemplates(templates) {
  if (!Array.isArray(templates)) {
    return []
  }

  return templates
    .map((template) => normalize1CTemplate(template))
    .filter((template) => template !== null)
}

/**
 * Extract structured sales rows from the supported backend payloads.
 *
 * @param {unknown} payload
 * @returns {Array<Record<string, any>>}
 * @throws {Error} When the backend response omits the authoritative structured sales rows.
 * @precondition Payload comes from `/api/services/sales/load`.
 * @postcondition Returns the authoritative array of structured sales rows for SPA consumers.
 * @mutates none
 */
export function normalizeSalesLoadResponse(payload) {
  if (Array.isArray(payload)) {
    return payload
  }

  if (payload && typeof payload === "object" && Array.isArray(payload.sales)) {
    return payload.sales
  }

  throw new Error("Invalid sales load response: missing structured sales rows")
}

/**
 * Create services client for 1C integration
 *
 * @returns {Object} Services client with all 1C integration methods
 *
 * @example
 * const servicesClient = createServicesClient()
 * const results = await servicesClient.searchSales("Pink Floyd")
 */
export function createServicesClient() {
  return {
    /**
     * Search sales in 1C system
     *
     * Makes GET request to search sales by query string.
     * Returns array of matching sales records from 1C.
     *
     * @param {string} query - Search query (artist name, title, catalog number, etc.)
     * @returns {Promise<Array>} Array of sales records from 1C
     * @throws {Error} When API request fails (network errors, server errors, 401/403)
     *
     * @example
     * const results = await servicesClient.searchSales("Pink Floyd - Dark Side of the Moon")
     * // Returns: [{ id: "123", artist: "Pink Floyd", title: "Dark Side of the Moon", ... }]
     *
     * @example
     * const results = await servicesClient.searchSales("BERTUS123")
     * // Search by catalog number
     */
    async searchSales(query) {
      const response = await api.get(ENDPOINTS.SALES, {
        params: { q: query },
      })
      return response.data
    },

    async loadSales(payload) {
      const response = await api.post(ENDPOINTS.LOAD_SALES, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      return normalizeSalesLoadResponse(response.data)
    },

    /**
     * Save data to 1C system
     *
     * Makes POST request to save order/inventory data to 1C.
     * Creates or updates records in 1C database.
     *
     * @param {Object} data - Data to save to 1C
     * @param {string} [data.type] - Type of data (order, inventory, etc.)
     * @param {Object} [data.payload] - Actual data payload
     * @returns {Promise<Object>} Response with save status and 1C record ID
     * @throws {Error} When API request fails or validation errors occur
     *
     * @example
     * const result = await servicesClient.saveTo1C({
     *   type: "order",
     *   payload: { orderId: 12345, items: [...] }
     * })
     * // Returns: { success: true, recordId: "1C-67890", timestamp: "2026-02-20T10:30:00Z" }
     */
    async saveTo1C(data) {
      const response = await api.post(ENDPOINTS.SUBMIT_SALES, data)
      return response.data
    },

    async submitSales(payload) {
      const response = await api.post(ENDPOINTS.SUBMIT_SALES, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      return response.data
    },

    /**
     * Get 1C documents list
     *
     * Makes GET request to retrieve all documents from 1C system.
     * Returns paginated list of 1C documents with metadata.
     *
     * @returns {Promise<Array>} Array of 1C documents
     * @throws {Error} When API request fails or user lacks permissions
     *
     * @example
     * const documents = await servicesClient.get1CDocuments()
     * // Returns: [
     * //   { id: "1", type: "invoice", date: "2026-02-20", status: "processed" },
     * //   { id: "2", type: "order", date: "2026-02-19", status: "pending" }
     * // ]
     */
    async get1CDocuments() {
      const response = await api.get(ENDPOINTS.GET_DOCUMENTS)
      return response.data
    },

    /**
     * Get 1C templates list
     *
     * Makes GET request to retrieve available document templates from 1C.
     * Templates define document structures for various 1C operations.
     *
     * @returns {Promise<Array>} Array of available 1C templates
     * @throws {Error} When API request fails or templates cannot be retrieved
     *
     * @example
     * const templates = await servicesClient.get1CTemplates()
     * // Returns: [
     * //   { id: "invoice_template", name: "Invoice Template", fields: ["date", "amount", ...] },
     * //   { id: "order_template", name: "Order Template", fields: ["customer", "items", ...] }
     * // ]
     */
    async get1CTemplates() {
      const response = await api.get(ENDPOINTS.GET_TEMPLATES)
      return normalize1CTemplates(response.data)
    },

    /**
     * Get 1C sales sources list
     *
     * Makes GET request to retrieve available sales sources from 1C.
     * Sources represent different channels or origins of sales data.
     *
     * @returns {Promise<Array>} Array of available sales sources
     * @throws {Error} When API request fails or sources cannot be retrieved
     *
     * @example
     * const sources = await servicesClient.get1CSources()
     * // Returns: ["site", "retail", "partner1", "partner2"]
     */
    async get1CSources() {
      const response = await api.get(ENDPOINTS.GET_SOURCES)
      return response.data
    },

    /**
     * Process revaluation Excel file
     *
     * Makes POST request with FormData to upload and process Excel file.
     * Handles inventory revaluation data in Excel format.
     *
     * @param {File} file - Excel file containing revaluation data
     * @returns {Promise<Object>} Processing results with updated records
     * @throws {Error} When file upload fails or processing errors occur
     *
     * @example
     * const file = document.querySelector('input[type="file"]').files[0]
     * const result = await servicesClient.processRevaluationExcel(file)
     * // Returns: {
     * //   success: true,
     * //   processed: 150,
     * //   updated: 145,
     * //   errors: 5,
     * //   details: [...]
     * // }
     */
    async processRevaluationExcel(file) {
      const formData = new FormData()
      formData.append("file", file)

      const response = await api.post(ENDPOINTS.PROCESS_REVALUATION, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })

      return response.data
    },

    /**
     * Get sales report from 1C
     *
     * Makes GET request to retrieve sales report data from 1C system.
     * Supports optional date range filtering.
     *
     * @param {Object} params - Report parameters
     * @param {string} [params.startDate] - Start date (ISO format)
     * @param {string} [params.endDate] - End date (ISO format)
     * @param {string} [params.groupBy] - Grouping field (day, week, month)
     * @returns {Promise<Object>} Sales report data
     * @throws {Error} When API request fails or invalid date range
     *
     * @example
     * const report = await servicesClient.getSalesReport({
     *   startDate: "2026-02-01",
     *   endDate: "2026-02-20",
     *   groupBy: "day"
     * })
     * // Returns: {
     * //   totalSales: 15000,
     * //   itemCount: 250,
     * //   breakdown: [{ date: "2026-02-01", amount: 750, count: 12 }, ...]
     * // }
     */
    async getSalesReport(params = {}) {
      const response = await api.get(ENDPOINTS.SALES, {
        params,
      })
      return response.data
    },
  }
}

// Singleton instance for direct import
export const servicesClient = createServicesClient()
